import { useMemo } from "react";
import { useSelector } from "react-redux";
import { CreditSelectors } from "Redux/CreditRedux";
import { Link } from "react-router-dom";

// Default credit costs per action (fallback if API doesn't provide)
const DEFAULT_COSTS = {
  image_generation: 1,
  video_generation: 1,
  audio_generation: 1,
  subtitle_generation: 0,
};

/**
 * Calculate estimated credit cost based on video settings
 */
function calculateEstimatedCost(settings, costs) {
  // Merge with defaults to handle null/undefined costs
  const effectiveCosts = { ...DEFAULT_COSTS, ...(costs || {}) };
  
  const duration = typeof settings.duration === 'number' ? settings.duration : 60;
  const imageFrequency = settings.imageFrequency || 5;
  
  // Calculate number of scenes/images
  let numScenes;
  if (imageFrequency === 0) {
    // One image for entire video
    numScenes = 1;
  } else {
    numScenes = Math.ceil(duration / imageFrequency);
  }
  
  // Base costs
  const imageCost = effectiveCosts.image_generation * numScenes;
  const audioCost = effectiveCosts.audio_generation;
  const subtitleCost = settings.subtitles ? effectiveCosts.subtitle_generation : 0;
  
  // Animation costs
  let animationCost = 0;
  if (settings.animateVisuals) {
    const videoCostPerScene = effectiveCosts.video_generation;
    if (settings.animationScope === "first_segment") {
      animationCost = videoCostPerScene; // Only first segment
    } else {
      animationCost = videoCostPerScene * numScenes; // All segments
    }
  }
  
  const totalCost = imageCost + audioCost + subtitleCost + animationCost;
  
  return {
    numScenes,
    breakdown: {
      images: { count: numScenes, cost: imageCost },
      audio: { count: 1, cost: audioCost },
      subtitles: { count: settings.subtitles ? 1 : 0, cost: subtitleCost },
      animations: { 
        count: settings.animateVisuals ? (settings.animationScope === "first_segment" ? 1 : numScenes) : 0, 
        cost: animationCost 
      },
    },
    total: totalCost,
  };
}

export default function CreditCostEstimate({ settings, className = "" }) {
  const credits = useSelector(CreditSelectors.getCredits);
  const costs = useSelector(CreditSelectors.getCosts);
  
  const availableCredits = credits?.available || 0;
  
  const estimate = useMemo(() => {
    return calculateEstimatedCost(settings, costs);
  }, [settings, costs]);
  
  const canAfford = availableCredits >= estimate.total;
  const shortfall = Math.max(0, estimate.total - availableCredits);
  
  return (
    <div className={`bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200 p-4 ${className}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-lg" aria-hidden="true">💰</span>
          <h3 className="text-sm font-semibold text-gray-800">Estimated Cost</h3>
        </div>
        <div className="text-right">
          <span className={`text-xl font-bold ${canAfford ? 'text-blue-600' : 'text-red-600'}`}>
            {estimate.total}
          </span>
          <span className="text-sm text-gray-500 ml-1">credits</span>
        </div>
      </div>
      
      {/* Cost breakdown */}
      <div className="space-y-1.5 mb-3">
        <div className="flex justify-between text-xs">
          <span className="text-gray-600">
            🖼️ Images ({estimate.breakdown.images.count} scenes)
          </span>
          <span className="text-gray-700 font-medium">{estimate.breakdown.images.cost}</span>
        </div>
        
        <div className="flex justify-between text-xs">
          <span className="text-gray-600">🔊 Audio narration</span>
          <span className="text-gray-700 font-medium">{estimate.breakdown.audio.cost}</span>
        </div>
        
        {estimate.breakdown.subtitles.cost > 0 && (
          <div className="flex justify-between text-xs">
            <span className="text-gray-600">📝 Subtitles</span>
            <span className="text-gray-700 font-medium">{estimate.breakdown.subtitles.cost}</span>
          </div>
        )}
        
        {estimate.breakdown.animations.cost > 0 && (
          <div className="flex justify-between text-xs">
            <span className="text-gray-600">
              ✨ Animations ({estimate.breakdown.animations.count} {estimate.breakdown.animations.count === 1 ? 'scene' : 'scenes'})
            </span>
            <span className="text-gray-700 font-medium">{estimate.breakdown.animations.cost}</span>
          </div>
        )}
      </div>
      
      {/* Available credits and status */}
      <div className="pt-3 border-t border-blue-200">
        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-500">
            Your balance: <span className="font-medium text-gray-700">{availableCredits} credits</span>
          </span>
          {canAfford ? (
            <span className="text-xs text-green-600 font-medium flex items-center gap-1">
              <span>✓</span> Sufficient credits
            </span>
          ) : (
            <span className="text-xs text-red-600 font-medium flex items-center gap-1">
              <span>⚠️</span> Need {shortfall} more
            </span>
          )}
        </div>
        
        {!canAfford && (
          <Link
            to="/plans"
            className="mt-2 block w-full text-center text-xs font-medium text-white bg-primary-500 hover:bg-primary-600 rounded-lg py-2 transition-colors"
          >
            Get More Credits
          </Link>
        )}
      </div>
    </div>
  );
}

export { calculateEstimatedCost };
